import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';

import type { TSelectedTool, TToolType, TToolsListItem } from '@intone/shared/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useToolsList,
  useTool,
  useDeleteTool,
  useDuplicateTool,
  useCreateTool,
} from '@/entities/tool';
import { useToolTypes } from '@/entities/toolType';
import { EntityList } from '@/widgets/entityList';
import { useShowDialog, UnsavedChangesDialogTexts } from '@/hooks/useShowDialog';

import { ToolEditor } from './ToolEditor';
import { UnsavedChangesProvider, useUnsavedChanges } from './UnsavedChangesContext';

function ToolsPageContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showTypeDialog, setShowTypeDialog] = useState(false);
  const { hasUnsavedChanges, setHasUnsavedChanges } = useUnsavedChanges();

  const toolIdParam = searchParams.get('tool_id')
  const selectedToolId = toolIdParam ? parseInt(toolIdParam, 10) : null

  const { data: toolsList = [], isLoading: isLoadingTools } = useToolsList()
  const { data: selectedTool } = useTool(selectedToolId)
  const { data: toolTypes = [], isLoading: isLoadingTypes } = useToolTypes()
  const deleteTool = useDeleteTool();
  const duplicateTool = useDuplicateTool();
  const createTool = useCreateTool();

  const shouldBlock = hasUnsavedChanges && deleteTool.status !== 'pending' && duplicateTool.status !== 'pending';
  const { showConfirmDialog } = useShowDialog(shouldBlock);

  const isLoading = isLoadingTools || isLoadingTypes;

  const handleSelectTool = (item: TToolsListItem) => {
    if (hasUnsavedChanges) {
      if (!showConfirmDialog(UnsavedChangesDialogTexts.toolsChange)) return;
      setHasUnsavedChanges(false);
    }
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tool_id', item.id.toString());
    setSearchParams(newSearchParams);
  };

  const handleDuplicateTool = (item: TToolsListItem) => {
    if (hasUnsavedChanges) {
      if (!showConfirmDialog(UnsavedChangesDialogTexts.toolsChange)) return;
      setHasUnsavedChanges(false);
    }
    duplicateTool.mutate(
      { id: item.id, newName: `${item.name}_(copy)` },
      {
        onSuccess: (result) => {
          toast.success('Tool duplicated successfully');
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set('tool_id', result.tool_id.toString());
          setSearchParams(newSearchParams);
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : 'Failed to duplicate tool');
        },
      }
    );
  };

  const handleDeleteTool = (item: TToolsListItem) => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this tool? This action cannot be undone.'
    );
    if (!confirmed) return;
    deleteTool.mutate(item.id, {
      onSuccess: () => {
        toast.success('Tool deleted successfully');
        if (selectedToolId === item.id) {
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('tool_id');
          setSearchParams(newSearchParams);
        }
      },
      onError: (error) => {
        toast.error(error instanceof Error ? error.message : 'Failed to delete tool');
      },
    });
  };

  const handleCreateNew = () => {
    if (hasUnsavedChanges) {
      if (!showConfirmDialog(UnsavedChangesDialogTexts.toolsChange)) return;
      setHasUnsavedChanges(false);
    }
    setShowTypeDialog(true);
  };

  const handleSelectToolType = (toolType: TToolType) => {
    setShowTypeDialog(false);
    createTool.mutate(
      {
        name: `New ${toolType.type_name.replace(/_/g, ' ')}`,
        description: null,
        tool_type_id: toolType.id,
        arguments: {},
      },
      {
        onSuccess: (result) => {
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set('tool_id', result.tool_id.toString());
          setSearchParams(newSearchParams);
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : 'Failed to create tool');
        },
      }
    );
  };

  const createButton = (
    <Button
      variant="outline"
      size="sm"
      onClick={handleCreateNew}
      disabled={createTool.isPending}
      className="w-full"
    >
      <Plus className="w-3 h-3 mr-2" />
      {createTool.isPending ? 'Creating...' : 'Create New Tool'}
    </Button>
  );

  return (
    <div className="w-full h-full p-6 overflow-hidden flex flex-col">
      <div className="mb-4 flex-shrink-0">
        <h1 className="text-heading font-bold text-gray-900 mb-1">Tools</h1>
        <p className="text-detail text-gray-600">
          Create and manage tools for your AI agents
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0">
        {/* Left Panel */}
        <EntityList<TToolsListItem>
          items={toolsList}
          selectedId={selectedToolId ?? undefined}
          getId={(t) => t.id}
          onSelect={handleSelectTool}
          onDuplicate={handleDuplicateTool}
          onDelete={handleDeleteTool}
          renderIcon={(t) =>
            t.icon_url ? (
              <img src={t.icon_url} alt="" className="w-4 h-4" />
            ) : null
          }
          renderPrimary={(t) => t.name || 'Unnamed'}
          renderSecondary={(t) =>
            t.tool_type_name ? (
              <div className="text-detail text-gray-500 truncate mt-0.5">
                {t.tool_type_name.replace(/_/g, ' ')}
              </div>
            ) : null
          }
          searchPlaceholder="Search tools..."
          searchFilter={(t, q) =>
            t.name.toLowerCase().includes(q) ||
            t.id.toString().includes(q) ||
            (t.tool_type_name ? t.tool_type_name.toLowerCase().includes(q) : false)
          }
          isLoading={isLoading}
          emptyMessage="No tools found"
          createButton={createButton}
        />

        {/* Right Panel */}
        <div className="flex-1 overflow-y-auto">
          {selectedToolId ? (
            <ToolEditor
              tool={selectedTool as TSelectedTool}
              toolTypes={toolTypes as TToolType[]}
            />
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
              <p className="text-body">Select a tool to start editing or create a new one</p>
            </div>
          )}
        </div>
      </div>

      <Dialog open={showTypeDialog} onOpenChange={setShowTypeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Select Tool Type</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2 max-h-[400px] overflow-y-auto">
            {(toolTypes as TToolType[]).map((tt) => (
              <Button
                key={tt.id}
                variant="outline"
                onClick={() => handleSelectToolType(tt)}
                className="justify-start h-auto py-2 px-3"
              >
                <div className="flex items-center gap-3">
                  {tt.icon_url && (
                    <img src={tt.icon_url} alt="" className="w-5 h-5" />
                  )}
                  <span className="text-body">{tt.type_name.replace(/_/g, ' ')}</span>
                </div>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ToolsPage() {
  return (
    <UnsavedChangesProvider>
      <ToolsPageContent />
    </UnsavedChangesProvider>
  );
}